import useUrlView from '../../ui/useUrlView';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { t } from '../../utils/i18n';
import { displayUserEmail, formatDateTime } from '../../utils/format';
import UserAvatar from '../../components/UserAvatar';
import UserDetailModal from '../../components/UserDetailModal/UserDetailModal';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { getCelebs } from '../../api/authAdmin';
import PaginatedTableCard from '../../components/PaginatedTableCard/PaginatedTableCard';
import UserRowActionsMenu from '../../components/UserRowActionsMenu/UserRowActionsMenu';
import UserBanDialog from '../../components/UserBanDialog/UserBanDialog';
import PartnerCelebDialog from '../../components/PartnerCelebDialog/PartnerCelebDialog';
import { useUserBanActions } from '../../utils/useUserBanActions';
import { usePartnerCelebActions } from '../../utils/usePartnerCelebActions';

const CELEB_TABLE_SKELETON_WIDTHS = [
  'w-10',
  'w-12',
  'w-24',
  'w-28',
  'w-32',
  'w-20',
  'w-14',
  'w-20',
  'w-20',
  'w-24',
  'w-16',
];

const PAGE_SIZE = 10;

function getAccountStatusLabel(status) {
  return status === 1 ? t('user.accountStatus.active') : t('user.accountStatus.inactive');
}

function getLoginStatusLabel(status) {
  return status === 1 ? t('user.loginStatus.loggedIn') : t('user.loginStatus.loggedOut');
}

function getIsCelebLabel(isCeleb) {
  const value = Number(isCeleb ?? 0);
  if (value === 2) {
    return t('celeb.isCeleb.partnerCeleb');
  }
  if (value === 1) {
    return t('celeb.isCeleb.celeb');
  }
  return '—';
}

function IsCelebBadge({ isCeleb }) {
  const value = Number(isCeleb ?? 0);
  if (value !== 1 && value !== 2) {
    return <span className="text-dark-text-secondary text-sm">—</span>;
  }
  const isPartner = value === 2;
  const classes = isPartner
    ? 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
    : 'bg-dark-surface text-dark-text-secondary border border-dark-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${classes}`}>
      {getIsCelebLabel(value)}
    </span>
  );
}

export default function CelebPage() {
  const [columnView,setColumnView] = useUrlView('columns',['basic','diagnostics'],'basic');
  const diagnostics = columnView === 'diagnostics';
  const navigate = useNavigate();
  const { role, token, cacheSession, logout } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const isAdmin = role === 'admin';
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [excludeResigned, setExcludeResigned] = useState(false);
  const [isCelebFilter, setIsCelebFilter] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [actionsMenuUserId, setActionsMenuUserId] = useState(null);

  const celebsQuery = useQuery({
    queryKey: ['celebs', apiEnvironment, page, PAGE_SIZE, searchQuery, excludeResigned, isCelebFilter],
    queryFn: () =>
      getCelebs(page, PAGE_SIZE, token, searchQuery, {
        excludeResigned,
        isCeleb: isCelebFilter === '' ? undefined : Number(isCelebFilter),
      }),
    enabled: !!token,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (celebsQuery.error?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [celebsQuery.error, logout, navigate]);

  const {
    items: paginatedCelebs = [],
    total_count: totalCount = 0,
    page: currentPage = 1,
    page_size: pageSize = PAGE_SIZE,
  } = celebsQuery.data || {};

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    setActionsMenuUserId(null);
  }, [page, searchQuery, excludeResigned, isCelebFilter]);

  const handleExcludeResignedChange = (e) => {
    setExcludeResigned(e.target.checked);
    setPage(1);
  };

  const handleIsCelebFilterChange = (e) => {
    setIsCelebFilter(e.target.value);
    setPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchQuery(searchDraft.trim());
  };

  const handleSearchClear = () => {
    setSearchDraft('');
    setSearchQuery('');
    setPage(1);
  };

  const handleModalUnauthorized = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const accountType = (u) => u?.account_type ?? u?.AccountType ?? 'USER';
  const canShowTicketDetail = (u) => accountType(u) !== 'REVIEWER';
  const handleUserClick = (user) => {
    if (!canShowTicketDetail(user)) return;
    setSelectedUserId(user.user_id ?? user.UserID);
  };
  const closeModal = () => setSelectedUserId(null);
  const closeActionsMenu = () => setActionsMenuUserId(null);

  const { runBanAction, isPending: isBanPending, pendingUserId, dialog, confirmDialog, closeDialog } =
    useUserBanActions({
      token,
      apiEnvironment,
      role,
      cacheSession,
      onUnauthorized: handleModalUnauthorized,
    });

  const {
    runPartnerCelebAction,
    isPending: isPartnerCelebPending,
    pendingUserId: partnerCelebPendingUserId,
    dialog: partnerCelebDialog,
    confirmDialog: confirmPartnerCelebDialog,
    closeDialog: closePartnerCelebDialog,
  } = usePartnerCelebActions({
    token,
    apiEnvironment,
    onUnauthorized: handleModalUnauthorized,
  });

  return (
    <div className="min-h-screen py-8 px-3 sm:px-5 lg:px-6 bg-dark-bg">
      <div className="w-full max-w-[min(100%,1920px)] mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-dark-text flex items-center gap-2">
              {t('celeb.title')}
              {isAdmin && (
                <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/40">
                  Admin
                </span>
              )}
            </h1>
            <p className="text-dark-text-secondary text-sm mt-1">{t('celeb.description')}</p>
          </div>
        </div>

        {celebsQuery.isError && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {celebsQuery.error?.message}
          </div>
        )}

        <form
          onSubmit={handleSearchSubmit}
          className="mb-4 rounded-xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface/40 p-4 shadow-sm"
        >
          <label htmlFor="celeb-search" className="block text-xs font-medium tracking-wide text-dark-text-secondary uppercase">
            {t('celeb.search.label')}
          </label>
          <div className="mt-2 w-full max-w-lg">
            <div className="flex min-h-[2.75rem] w-full min-w-0 overflow-hidden rounded-lg border border-dark-border bg-dark-bg shadow-inner transition-colors focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/35">
              <input
                id="celeb-search"
                type="search"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder={t('celeb.search.placeholder')}
                className="min-w-0 flex-1 border-0 bg-transparent py-2.5 pl-4 pr-2 text-sm text-dark-text placeholder:text-dark-text-secondary/60 outline-none focus:ring-0"
                autoComplete="off"
              />
              <div className="flex shrink-0 divide-x divide-dark-border/80">
                <button
                  type="submit"
                  aria-label={t('celeb.search.submit')}
                  title={t('celeb.search.submit')}
                  className="flex items-center justify-center bg-primary/15 px-3.5 text-lg leading-none text-primary transition-colors hover:bg-primary/25 active:bg-primary/35"
                >
                  🔍
                </button>
                <button
                  type="button"
                  onClick={handleSearchClear}
                  aria-label={t('celeb.search.clear')}
                  title={t('celeb.search.clear')}
                  className="flex items-center justify-center bg-dark-surface/50 px-3.5 text-lg leading-none text-dark-text-secondary transition-colors hover:bg-dark-surface hover:text-primary"
                >
                  🗑️
                </button>
              </div>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-dark-text-secondary/90">{t('celeb.search.hint')}</p>
          </div>
        </form>

        <div className="mb-4 rounded-xl border border-dark-border bg-dark-card p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <label className="text-xs text-dark-text-secondary flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={excludeResigned}
              onChange={handleExcludeResignedChange}
              className="accent-primary"
            />
            <span>{t('celeb.filter.excludeResigned')}</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-dark-text-secondary">{t('celeb.table.isCeleb')}</span>
            <select
              value={isCelebFilter}
              onChange={handleIsCelebFilterChange}
              className="px-2 py-1.5 rounded-lg bg-dark-bg border border-dark-border text-dark-text text-xs focus:outline-none focus:border-primary/60"
            >
              <option value="">{t('celeb.filter.isCelebAll')}</option>
              <option value="1">{t('celeb.filter.celeb')}</option>
              <option value="2">{t('celeb.filter.partnerCeleb')}</option>
            </select>
          </div>
        </div>

        <label className="mb-4 inline-flex items-center gap-2 text-sm text-dark-text-secondary"><input type="checkbox" checked={diagnostics} onChange={event => setColumnView(event.target.checked ? 'diagnostics' : 'basic')} />{t('adminUi.diagnosticView')}</label>
        <PaginatedTableCard
          error={celebsQuery.error}
          dataUpdatedAt={celebsQuery.dataUpdatedAt}
          isPending={celebsQuery.isPending}
          isFetching={celebsQuery.isFetching}
          isEmpty={!celebsQuery.isPending && paginatedCelebs.length === 0}
          skeletonRows={PAGE_SIZE}
          skeletonCellWidths={CELEB_TABLE_SKELETON_WIDTHS}
          colSpan={diagnostics ? 11 : 7}
          minHeight="min-h-[32rem]"
          tableClassName={diagnostics ? "w-full min-w-[980px]" : "w-full min-w-[800px]"}
          loadingLabel={t('celeb.table.loading') || t('common.loading')}
          emptyLabel={t('celeb.table.empty')}
          page={page}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setPage}
          prevLabel={t('celeb.pagination.prev')}
          nextLabel={t('celeb.pagination.next')}
          summary={t('celeb.pagination.page', { page: currentPage, totalPages })}
          header={(
            <tr className="border-b border-dark-border">
              <th className="text-left py-3 px-2 text-dark-text-secondary font-medium text-sm w-14 min-w-14">{t('celeb.table.avatar')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celeb.table.userId')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celeb.table.username')}</th>
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celeb.table.name')}</th>}
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celeb.table.email')}</th>}
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celeb.table.isCeleb')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celeb.table.fanCount')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celeb.table.accountStatus')}</th>
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celeb.table.loginStatus')}</th>}
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celeb.table.createdAt')}</th>}
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm whitespace-nowrap">{t('celeb.table.actions')}</th>
            </tr>
          )}
        >
          {paginatedCelebs.map((user) => (
            <tr
              key={user.user_id ?? user.UserID}
              className="border-b border-dark-border last:border-0 hover:bg-dark-surface/50"
            >
              <td className="py-3 px-2 align-middle w-14 min-w-14">
                <UserAvatar user={user} size={32} />
              </td>
              <td
                className={`py-3 px-4 text-primary ${canShowTicketDetail(user) ? 'cursor-pointer underline-offset-2 hover:underline' : 'cursor-default opacity-90'}`}
                onClick={() => handleUserClick(user)}
                role={canShowTicketDetail(user) ? 'button' : undefined}
              >
                {user.user_id ?? user.UserID ?? '-'}
              </td>
              <td className="py-3 px-4 text-dark-text">{user.username ?? user.Username ?? '-'}</td>
              {diagnostics && <td className="py-3 px-4 text-dark-text">{user.name ?? user.Name ?? '-'}</td>}
              {diagnostics && <td className="py-3 px-4 text-dark-text text-sm">{displayUserEmail(user)}</td>}
              <td className="py-3 px-4">
                <IsCelebBadge isCeleb={user.is_celeb ?? user.IsCeleb} />
              </td>
              <td className="py-3 px-4 text-dark-text tabular-nums">
                {user.fan_count ?? user.FanCount ?? 0}
              </td>
              <td className="py-3 px-4">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    (user.account_status ?? user.AccountStatus) === 1 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                  }`}
                >
                  {getAccountStatusLabel(user.account_status ?? user.AccountStatus ?? 0)}
                </span>
              </td>
              {diagnostics && <td className="py-3 px-4">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    (user.login_status ?? user.LoginStatus) === 1 ? 'bg-blue-500/10 text-blue-400' : 'bg-dark-surface text-dark-text-secondary'
                  }`}
                >
                  {getLoginStatusLabel(user.login_status ?? user.LoginStatus ?? 0)}
                </span>
              </td>}
              {diagnostics && <td className="py-3 px-4 text-dark-text-secondary text-sm">
                {formatDateTime(user.created_at ?? user.CreatedAt)}
              </td>}
              <td className="py-3 px-4 align-middle whitespace-nowrap overflow-visible">
                <UserRowActionsMenu
                  user={user}
                  isOpen={actionsMenuUserId === (user.user_id ?? user.UserID)}
                  onToggle={() => {
                    const id = user.user_id ?? user.UserID;
                    setActionsMenuUserId((prev) => (prev === id ? null : id));
                  }}
                  onClose={closeActionsMenu}
                  onViewDetail={() => handleUserClick(user)}
                  onBanAction={runBanAction}
                  onPartnerCelebAction={runPartnerCelebAction}
                  canViewDetail={canShowTicketDetail(user)}
                  isBanPending={isBanPending}
                  isPartnerCelebPending={isPartnerCelebPending}
                  pendingUserId={pendingUserId}
                  partnerCelebPendingUserId={partnerCelebPendingUserId}
                />
              </td>
            </tr>
          ))}
        </PaginatedTableCard>

        {selectedUserId && token && (
          <UserDetailModal
            userId={selectedUserId}
            token={token}
            onClose={closeModal}
            onUnauthorized={handleModalUnauthorized}
            onNavigateUserId={setSelectedUserId}
            initialUser={paginatedCelebs.find((u) => (u.user_id ?? u.UserID) === selectedUserId)}
            runBanAction={runBanAction}
            isBanPending={isBanPending}
            pendingUserId={pendingUserId}
            runPartnerCelebAction={runPartnerCelebAction}
            isPartnerCelebPending={isPartnerCelebPending}
            partnerCelebPendingUserId={partnerCelebPendingUserId}
          />
        )}

        <UserBanDialog
          dialog={dialog}
          onConfirm={confirmDialog}
          onCancel={closeDialog}
          loading={isBanPending}
        />

        <PartnerCelebDialog
          dialog={partnerCelebDialog}
          onConfirm={confirmPartnerCelebDialog}
          onCancel={closePartnerCelebDialog}
          loading={isPartnerCelebPending}
        />
      </div>
    </div>
  );
}
